import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { advisorView } from 'Context/ViewContext';
import { PersonalInfo as SectionKey } from 'Consts/Questionnaire';
import TextInputGroup from 'Components/Universal/Questionnaire/Fields/TextInputGroup';
import useSimpleHousehold from 'Hooks/useSimpleHousehold';
import fr from 'facet-rest';
import TextInput from '../Fields/TextInput';
import SocialSecurityInput from '../Fields/SocialSecurityInput';
import DateInput from '../Fields/DateInput';
import SelectInput from '../Fields/SelectInput';
import Section from '../Section';
import USStateInput from '../Fields/USStateInput';

const PersonalInformation = ({
  handleMaritalStatusChange,
}) => {
  const citizenDetails = 'citizenDetails';
  const usaAlpha3 = 'USA';
  const [clientMetaState, setClientMetaState] = useState();
  const [showResidentType, setShowResidentType] = useState(false);

  const { householdResource } = useSimpleHousehold();

  const getClientMeta = async (hhResource) => {
    const clientsCollection = await hhResource.relationship(fr.relationships.clients.name).getRelated();
    setClientMetaState(clientsCollection.meta());
  };

  useEffect(() => {
    if (!householdResource) {
      return;
    }

    getClientMeta(householdResource);
  }, [householdResource]);

  if (!clientMetaState) {
    return null;
  }

  const citizenshipOptions = clientMetaState.field('citizenshipDetails').options().list().map(option => ({
    id: option.id(),
    value: option.attr('prompt'),
  }));
  citizenshipOptions.unshift({ id: '', value: '- Please select -' });

  const residentTypeOptions = clientMetaState.field('residentType').options().list().map(option => ({
    id: option.id(),
    value: option.attr('prompt'),
  }));
  residentTypeOptions.unshift({ id: '', value: '- Please select -' });

  const handleCitizenshipChange = (k, v) => {
    if (k !== citizenDetails) return;
    setShowResidentType(v !== usaAlpha3 && v !== '');
  };

  return (
    <Section
      expanded
      transitionSpeed={0.25}
      title="Personal Information"
      sectionKey={SectionKey}
    >
      <TextInputGroup
        inputConfigs={[
          {
            label: 'First Name',
            isRequired: true,
            answerKey: 'clientFirstName',
          },
          {
            label: 'Preferred Name',
            answerKey: 'clientNickname',
          },
          {
            label: 'Middle Name',
            answerKey: 'clientMiddleName',
          },
          {
            label: 'Last Name',
            isRequired: true,
            answerKey: 'clientLastName',
          },
        ]}
      />
      <SocialSecurityInput
        placeholder="Social security number"
        name="socialSecurityNumber"
        answerKey="clientSSN"
      />
      <DateInput
        placeholder="Date of birth"
        requiredView={advisorView}
        name="dateOfBirth"
        answerKey="clientDOB"
        mode="year"
        displayInLocalTime={false}
      />
      <SelectInput
        label="Gender"
        name="clientGender"
        answerKey="clientGender"
        requiredView={advisorView}
        options={[
          { id: '', value: '- Please select -' },
          { id: 'female', value: 'Female' },
          { id: 'male', value: 'Male' },
          { id: 'other', value: 'Other' },
        ]}
      />
      <SelectInput
        label="Marital status"
        name="maritalStatus"
        answerKey="clientMaritalStatus"
        cannotBlankFieldOut
        options={[
          { id: '', value: '- Please select -' },
          { id: 'single', value: 'Single' },
          { id: 'married', value: 'Married' },
          {
            id: 'significantOther',
            value: 'Significant other',
          },
          { id: 'divorced', value: 'Divorced' },
          { id: 'widow/er', value: 'Widow(er)' },
        ]}
        onChange={handleMaritalStatusChange}
      />
      <SelectInput
        label="Country of Citizenship"
        name={citizenDetails}
        answerKey="clientCitizenshipDetails"
        options={citizenshipOptions}
        onChange={handleCitizenshipChange}
      />
      {showResidentType && (
        <SelectInput
          label="Resident Type"
          name="residentType"
          answerKey="clientResidentType"
          options={residentTypeOptions}
        />
      )}
      <TextInput
        requiredView={advisorView}
        placeholder="Drivers license number"
        name="drivesLicenseNumber"
        answerKey="clientDriversLicenseNumber"
      />
      <USStateInput
        requiredView={advisorView}
        label="Drivers license state"
        name="drivesLicenseState"
        answerKey="clientDriversLicenseState"
      />
      <DateInput
        requiredView={advisorView}
        placeholder="Drivers license issue date"
        name="drivesLicenseIssue"
        answerKey="clientDriversLicenseIssue"
      />
      <DateInput
        requiredView={advisorView}
        placeholder="Drivers license expiration date"
        name="drivesLicenseExpires"
        answerKey="clientDriversLicenseExpires"
      />
    </Section>
  );
};

PersonalInformation.propTypes = {
  handleMaritalStatusChange: PropTypes.func.isRequired,
};

export default PersonalInformation;
